import React from 'react';

import { useIntl } from 'react-intl';

/* eslint-disable-next-line @atlassian/tangerine/import/entry-points -- legacy onboarding baseline */
import IconTile from '@atlaskit/icon/icon-tile';
import type { IconSize, IconTileProps } from '@atlaskit/icon/types';
import { type ContentType } from '@atlassian/conversation-assistant-store/controllers/preview-content/contentType';

import { getPreviewItemIconConfig } from './content-type-icon-config';

type CommonProps = {
	contentType: ContentType | null;
};

/**
 * The tile and plain variants render different ADS components (IconTile vs a
 * bare icon) whose `size` scales are not interchangeable, so each variant
 * carries its own `size` type.
 *
 * - 'tile' (default) renders the coloured IconTile — `bold` picks its saturated-background
 *   appearance (the preview card header's existing look) vs its non-bold, neutral-background
 *   appearance (colour carried by the icon glyph only).
 * - 'plain' renders a bare icon with no tile/background at all, for spots that just need the glyph.
 */
type PreviewItemIconProps =
	| (CommonProps & { variant?: 'tile'; bold?: boolean; size?: IconTileProps['size'] })
	| (CommonProps & { variant: 'plain'; size?: IconSize });

/**
 * Renders the icon for a preview item's content type. Consumers never decide
 * which icon or colour to use — that lives in getPreviewItemIconConfig — they
 * only choose variant/bold/size for their layout. Renders nothing when the
 * content type has no icon.
 */
export const PreviewItemIcon = (props: PreviewItemIconProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const config = getPreviewItemIconConfig(props.contentType);

	if (!config) {
		return null;
	}

	const label = formatMessage(config.altText);

	if (props.variant === 'plain') {
		const Icon = config.icon;
		return <Icon label={label} size={props.size} />;
	}

	return (
		<IconTile
			icon={config.icon}
			label={label}
			appearance={props.bold ? config.boldAppearance : config.appearance}
			size={props.size}
		/>
	);
};
